import { Popconfirm, Switch, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { NOTE_TYPES } from './fields';

const { Text } = Typography;

// The things an operator DOES to a note, as opposed to what it types into it (TL, 2026-09-10): open or
// close it to investors, and mark its loan repaid. Each one is a SWITCH (TL, 2026-09-12) — two buttons
// never said which state the note was IN, nor that a closed note can be listed again — with the state
// written beside it, a confirmation that says what the investor will see, and a save of ONLY its own
// column: `adminUpdateInvestment(data: { status })` or `{ loanStatus }`, through the page's `onSave`.
//
// THEY ARE CONTROLS, NOT A SECTION (TL, 2026-09-12): they used to sit in an "Operations" card stacked
// above the form, which made two toggles weigh as much as the whole fact sheet. They now ride INLINE in
// the note's header (NoteHeader) — label, switch, state — with the same confirmations and the same copy.
// The "why" line each one carried is the tooltip on its label.
// The offering in Alto is NOT an operation here: it is created at the note's BIRTH (Add Property) and
// its state is read in the System tab (NoteAltoStatus), where the retry lives.
// A new note is born listed and active; `deleted` is written by the backend's adminDeleteInvestment
// (the panel does not offer it) and only reads here.

const AVAILABILITY = {
  on: 'Listed in storefront',
  off: 'Closed to new investments',
  onWhy: 'Investors see the note in the storefront and can buy into it.',
  offWhy: 'Hidden from investors: no new investments. It can be listed again at any time.',
  closeConfirm:
    'Closing hides the note from investors and stops new investments. You can list it again later.',
  openConfirm: 'Listing shows the note to investors again and accepts new investments.',
};

// Verified in the backend (transaction-item/modules/portfolio/dashboard.service): interest is accrued
// only while the note's loanStatus is `active`, and the investor's Active/Repaid lists are that same
// column — so this switch moves money's clock, not a label.
const LOAN = {
  on: 'Loan active (accruing interest)',
  off: 'Loan repaid (stops accrual)',
  onWhy: 'Interest accrues for every investor and the note sits in their Active positions.',
  offWhy: "Interest no longer accrues and the note sits in every investor's Repaid positions.",
  repaidConfirm:
    'Marks the loan as repaid: interest stops accruing for every investor and the note moves to their Repaid list.',
  activeConfirm:
    "Moves this note back to every investor's Active list and interest accrues again.",
  unset:
    'Not set: the note shows in neither the Active nor the Repaid list. Turn it on to mark the loan active.',
};

const DELETED_WHY =
  'Soft-deleted by the backend. It stays out of the storefront for history; it cannot be re-opened here.';
const SCHEDULER_OWNED =
  'Set by the note-cycle scheduler: a cycle opens on its start date and closes at rollover.';

// A switch that ASKS before it flips: its value is the stored column, so it never moves on its own —
// the click only opens the confirmation, and the new state arrives from the save.
function ConfirmSwitch({
  checked,
  disabled,
  busy,
  name,
  why,
  state,
  confirm,
  okText,
  cancelText,
  onConfirm,
}) {
  const [asking, setAsking] = useState(false);

  const apply = () => {
    setAsking(false);
    return onConfirm();
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Tooltip title={why}>
        <Text type="secondary" style={{ fontSize: 12, borderBottom: '1px dotted #bfbfbf' }}>
          {name}
        </Text>
      </Tooltip>
      <Popconfirm
        visible={asking}
        title={confirm}
        okText={okText}
        cancelText={cancelText}
        onConfirm={apply}
        onCancel={() => setAsking(false)}
        onVisibleChange={(open) => {
          if (!open) setAsking(false);
        }}
        disabled={disabled}
      >
        <Switch
          size="small"
          checked={checked}
          loading={busy}
          disabled={disabled || busy}
          onChange={() => setAsking(true)}
        />
      </Popconfirm>
      <Text style={{ fontSize: 12 }}>{state}</Text>
    </span>
  );
}

export function NoteOperations({ investment, onSave }) {
  const [busy, setBusy] = useState(false);
  const isConstitution = investment.noteType === NOTE_TYPES.constitution;
  const isDeleted = investment.status === 'deleted';
  const listed = investment.status === 'available_investment';
  const loanActive = investment.loanStatus === 'active';

  const run = async (data) => {
    setBusy(true);
    try {
      await onSave(data);
    } finally {
      setBusy(false);
    }
  };

  // The availability switch is the operator's only while nobody else owns the column: a deleted note is
  // the backend's, a Constitution cycle is the scheduler's.
  const availabilityLocked = isDeleted || isConstitution;
  let availabilityWhy = listed ? AVAILABILITY.onWhy : AVAILABILITY.offWhy;
  if (isDeleted) availabilityWhy = DELETED_WHY;
  else if (isConstitution) availabilityWhy = `${availabilityWhy} ${SCHEDULER_OWNED}`;

  let loanWhy = loanActive ? LOAN.onWhy : LOAN.offWhy;
  if (!investment.loanStatus) loanWhy = LOAN.unset;

  // One row, its own gaps: a `Space` in the header could not separate them — two children inside one
  // fragment are a single Space item.
  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 32px' }}
    >
      <ConfirmSwitch
        name="Availability"
        why={availabilityWhy}
        state={isDeleted ? 'Deleted' : listed ? AVAILABILITY.on : AVAILABILITY.off}
        checked={listed}
        disabled={availabilityLocked}
        busy={busy}
        confirm={listed ? AVAILABILITY.closeConfirm : AVAILABILITY.openConfirm}
        okText={listed ? 'Close' : 'List'}
        cancelText={listed ? 'Keep listed' : 'Keep closed'}
        onConfirm={() => run({ status: listed ? 'previously_funded' : 'available_investment' })}
      />
      <ConfirmSwitch
        name="Loan status"
        why={loanWhy}
        state={loanActive ? LOAN.on : investment.loanStatus ? LOAN.off : 'Not set'}
        checked={loanActive}
        busy={busy}
        confirm={loanActive ? LOAN.repaidConfirm : LOAN.activeConfirm}
        okText={loanActive ? 'Mark repaid' : 'Mark active'}
        cancelText="Cancel"
        onConfirm={() => run({ loanStatus: loanActive ? 'repaid' : 'active' })}
      />
    </div>
  );
}
